import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms'
import {
  AfterViewChecked,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core'
import { TrackingPreferences, TrackingSelectors } from '../tracking.model'
import { IControl } from 'src/app/management-information/store/management-information.reducer'
import { BasicControl } from 'src/app/management-information/model/management-information.model'
import { SelectedUserPreferences } from 'src/app/core/model/reinsurer.model'
import { MatSelectChange, MatSelect, MatSelectTrigger } from '@angular/material/select'

import { MultiselectAutocompleteComponent } from '../../shared/multiselect-autocomplete.component'
import { MatLabel, MatFormField, MatSuffix } from '@angular/material/form-field'
import { MatInput } from '@angular/material/input'
import {
  MatDatepickerInput,
  MatDatepickerToggle,
  MatDatepicker,
} from '@angular/material/datepicker'
import { MatProgressBar } from '@angular/material/progress-bar'
import { ButtonComponent } from '../../shared/button/button.component'
import { MatOption } from '@angular/material/autocomplete'
import { MatIcon } from '@angular/material/icon'
import { NgFor } from '@angular/common'
import { ConfirmationDialogService } from '@shared/services/confirmation-dialog.service'
import { MatDialog } from '@angular/material/dialog'
import { dateToIsoString } from 'src/app/shared/util/date'

@Component({
  selector: 'app-tracking-sidebar',
  templateUrl: './tracking-sidebar.component.html',
  styleUrls: ['./tracking-sidebar.component.scss'],
  imports: [
    ReactiveFormsModule,
    MultiselectAutocompleteComponent,
    MatLabel,
    MatFormField,
    MatInput,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatProgressBar,
    ButtonComponent,
    MatSelect,
    MatOption,
    MatIcon,
    NgFor,
    MatSelectTrigger,
  ],
})
export class TrackingSideBarComponent implements OnInit, OnChanges {
  private readonly formBuilder = inject(FormBuilder)
  private confirmationDialog = inject(ConfirmationDialogService)

  @ViewChild('controlsRef') controlsRef: ElementRef

  @Input() selectors: TrackingSelectors
  @Input() isLoading: boolean
  @Input() inceptionInterval: BasicControl
  @Input() userPreferences: SelectedUserPreferences[]
  @Input() autoSelectSavedView: boolean = true

  form: FormGroup
  minInceptionDate: Date
  maxInceptionDate: Date
  selectedPreferenceValue: number | null = null

  @Output() filtersChanged = new EventEmitter<{
    filters: Record<string, string[]>
    isSet: boolean
  }>()
  @Output() inceptionDateChanged = new EventEmitter<{
    minInceptionDate: string
    maxInceptionDate: string
  }>()
  @Output() saveTrackingPreferences = new EventEmitter<{ filters?: Record<string, string[]> }>()
  @Output() setTrackingPreferences = new EventEmitter<{ id: number }>()
  @Output() deleteTrackingPreferences = new EventEmitter<{
    deletedPreferences: SelectedUserPreferences
  }>()
  @Output() updateTrackingPreferences = new EventEmitter<{
    updatePreferences: SelectedUserPreferences
  }>()

  dialog = inject(MatDialog)
  preferenceNames: Record<number, string> = {}

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      clientName: new FormControl([]),
      year: new FormControl([]),
      businessUnit: new FormControl([]),
      reinsurerNames: new FormControl([]),
      complete: new FormControl([]),
    })
    this.minInceptionDate = new Date(this.inceptionInterval.minValue)
    this.maxInceptionDate = new Date(this.inceptionInterval.maxValue)
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.minInceptionDate = new Date(this.inceptionInterval.minValue)
    this.maxInceptionDate = new Date(this.inceptionInterval.maxValue)

    if (changes['autoSelectSavedView'] && !this.autoSelectSavedView) {
      this.selectedPreferenceValue = null
      this.form?.reset({
        clientName: [],
        year: [],
        businessUnit: [],
        reinsurerNames: [],
        complete: [],
      })
    }

    if (
      changes['autoSelectSavedView'] &&
      this.autoSelectSavedView &&
      this.userPreferences?.length
    ) {
      const latestPreference = this.userPreferences.slice().sort((a, b) => {
        const dateA = new Date(a.audit_update_dt ?? a.audit_insert_dt ?? 0).getTime()
        const dateB = new Date(b.audit_update_dt ?? b.audit_insert_dt ?? 0).getTime()
        return dateB - dateA
      })[0]
      this.selectedPreferenceValue = latestPreference.id!
      this.setTrackingPreferences.emit({ id: latestPreference.id! })
    }

    if (changes['userPreferences'] && this.userPreferences?.length) {
      this.preferenceNames = this.userPreferences.reduce(
        (acc, pref) => {
          const parsed = JSON.parse(pref.preferenceValue) as TrackingPreferences
          acc[pref.id] = parsed.label ?? 'Preference 1'
          return acc
        },
        {} as Record<number, string>,
      )
      if (this.autoSelectSavedView) {
        const latestPreference = this.userPreferences.slice().sort((a, b) => {
          const dateA = new Date(a.audit_update_dt ?? a.audit_insert_dt ?? 0).getTime()
          const dateB = new Date(b.audit_update_dt ?? b.audit_insert_dt ?? 0).getTime()
          return dateB - dateA
        })[0]

        this.selectedPreferenceValue = latestPreference.id!
      }
    }
  }

  dispatchFilters(): void {
    const filters = this.getFiltersFromControls()
    this.filtersChanged.emit({ filters, isSet: true })
    this.form.markAsPristine()
  }

  clearFilter(event: MouseEvent, filter: IControl): void {
    event.stopPropagation()
    this.selectedPreferenceValue = null
    this.form.controls[filter.columnName].setValue([])
    const filters = this.getFiltersFromControls()
    this.filtersChanged.emit({ filters, isSet: true })
  }

  intervalChanged(prefix: 'min' | 'max', value: string): void {
    const isMin = prefix === 'min'
    if (isMin) {
      this.minInceptionDate = new Date(value)
    } else {
      this.maxInceptionDate = new Date(value)
    }
    this.inceptionDateChanged.emit({
      minInceptionDate: dateToIsoString(this.minInceptionDate),
      maxInceptionDate: dateToIsoString(this.maxInceptionDate),
    })
  }

  savePreferences(): void {
    if (!this.autoSelectSavedView) {
      const filters = this.getFiltersFromControls()
      this.saveTrackingPreferences.emit({ filters })
    } else {
      this.saveTrackingPreferences.emit({})
    }
  }

  updatePreferences(): void {
    let selectedPreference = this.userPreferences.find(
      (up) => up.id === this.selectedPreferenceValue,
    )
    this.updateTrackingPreferences.emit({ updatePreferences: selectedPreference })
  }

  getName(id: number): string {
    if (!id) return 'Preference 1'
    return (
      (
        JSON.parse(
          this.userPreferences.find((u) => u.id === id).preferenceValue,
        ) as TrackingPreferences
      ).label ?? 'Preference 1'
    )
  }

  onPreferenceChange(change: MatSelectChange): void {
    if (!change.value) {
      this.selectedPreferenceValue = null
      return
    }
    this.selectedPreferenceValue = change.value
    this.setTrackingPreferences.emit({ id: change.value })
    let filterData: Record<string, string[]>
    let selectedPreference = JSON.parse(
      this.userPreferences.find((up) => up.id === change.value).preferenceValue,
    ) as TrackingPreferences
    if (selectedPreference) {
      filterData = {
        clientName: selectedPreference.clientName,
        year: selectedPreference.year,
        businessUnit: selectedPreference.businessUnit,
        reinsurerNames: selectedPreference.reinsurerNames,
        complete: selectedPreference.complete,
      }
      this.filtersChanged.emit({ filters: filterData, isSet: true })
    }
  }

  private getFiltersFromControls(): Record<string, string[]> {
    const filters = Object.entries(this.form.controls).reduce((acc, [key, formControl]) => {
      acc = {
        ...acc,
        [key]: formControl.value,
      }
      return acc
    }, {})
    return filters
  }

  trackByPreferenceId(index: number, pref: any) {
    return pref.id
  }

  deletePreference(preference: any, event: MouseEvent) {
    this.selectedPreferenceValue = null
    event.stopPropagation()
    const dialogRef = this.confirmationDialog.open({
      submitLabel: 'Confirm',
      message: 'You are about to permenantly delete this view, please confirm this action.',
    })
    dialogRef.afterClosed().subscribe((confirm) => {
      if (confirm) {
        const selectedId = preference?.id
        if (!selectedId) {
          console.warn('view ID is undefined')
          return
        }

        let selectedPreference = this.userPreferences.find((up) => up.id === selectedId)
        this.deleteTrackingPreferences.emit({ deletedPreferences: selectedPreference! })

        let updatedUserPreferences = this.userPreferences.filter((up) => up.id !== selectedId)

        if (updatedUserPreferences.length !== 0) {
          const latestPreference = updatedUserPreferences.slice().sort((a, b) => {
            const dateA = new Date(a.audit_update_dt ?? a.audit_insert_dt ?? 0).getTime()
            const dateB = new Date(b.audit_update_dt ?? b.audit_insert_dt ?? 0).getTime()
            return dateB - dateA
          })[0]
          this.setTrackingPreferences.emit({ id: latestPreference.id! })

          let filterData: Record<string, string[]>
          let selected = JSON.parse(latestPreference.preferenceValue) as TrackingPreferences
          if (selected) {
            filterData = {
              clientName: selected.clientName,
              year: selected.year,
              businessUnit: selected.businessUnit,
              reinsurerNames: selected.reinsurerNames,
              complete: selected.complete,
            }
            this.filtersChanged.emit({ filters: filterData, isSet: true })
          }
        }
      }
    })
  }
}
